import React from 'react';
import { DistributionBarProps, DistributionSegment } from '../../../types/DesignSystem/dataviz';
import { seriesClass, SERIES_COUNT } from '../dataviz';
import { withClassName } from '../cn';

/**
 * DistributionBar (§ 311) — how a whole divides, in one row.
 *
 * The stacked-bar-of-one: "the catalogue is 46% fasteners, 28% fittings, 14%
 * bearings, and 12% everything else". A pie would need its own panel and a legend to
 * be read at all; this fits in a table cell and still answers the question.
 *
 * **The remainder is computed, not assumed.** Pass `total` and any shortfall renders
 * as a "rest" slice in series 6 — the neutral, which is exactly the second job § 310
 * gave it. Without that, a caller whose segments cover 70% of the catalogue gets a
 * bar that silently rescales to 100% and misstates every share on screen.
 *
 * **Segments are `<div>`s, not an SVG.** A flex row with percentage widths reflows
 * with the container for free, and the 1px gaps between slices are a border rather
 * than geometry that has to be recomputed. An SVG would need a width measurement to
 * do the same thing, which is a ResizeObserver this does not need.
 */

const TRACK_H = {
  sm: 'tw-h-1.5',
  md: 'tw-h-2.5',
} as const;

interface Slice extends DistributionSegment {
  share: number;
  rest: boolean;
}

/**
 * Segments → drawable slices.
 *
 * Every branch here is a real arrival from real data:
 *
 * - **zero and negative values are dropped.** A 0% slice renders as a 0-width div
 *   that the 1px gap still draws, so a category with nothing in it would appear as a
 *   hairline the reader counts as a category.
 * - **an all-zero set returns nothing**, so the caller shows an empty track rather
 *   than a division by zero.
 * - **`total` below the sum wins anyway** — the sum becomes the denominator. A
 *   `total` smaller than its parts is a bug in the caller's arithmetic, and
 *   rescaling to it would render shares over 100%.
 */
export const toSlices = (
  segments: DistributionSegment[],
  total: number | undefined,
  restLabel: string,
): Slice[] => {
  const usable = segments.filter((s) => Number.isFinite(s.value) && s.value > 0);
  const sum = usable.reduce((acc, s) => acc + s.value, 0);
  if (sum <= 0) return [];

  const denominator = Number.isFinite(total) && (total as number) > sum ? (total as number) : sum;

  const slices: Slice[] = usable.map((s) => ({
    ...s,
    share: (s.value / denominator) * 100,
    rest: false,
  }));

  const remainder = denominator - sum;
  if (remainder > 0) {
    slices.push({
      label: restLabel,
      value: remainder,
      /* Series 6 by name, not by position: the rest slice is the neutral whatever
         how many categories preceded it. */
      series: SERIES_COUNT - 1,
      share: (remainder / denominator) * 100,
      rest: true,
    });
  }
  return slices;
};

const DistributionBar = React.forwardRef<HTMLDivElement, DistributionBarProps>(function DistributionBar({
  segments,
  total,
  label,
  size = 'md',
  showLegend = false,
  restLabel = 'Other',
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  const slices = toSlices(segments, total, restLabel);

  /* The announced string is the bar's content — the slices are aria-hidden shapes.
     Read out, it is the same sentence a person would say about the picture. */
  const spoken = slices.length === 0
    ? `${label}: no data`
    : `${label}: ${slices.map((s) => `${s.label} ${s.share.toFixed(0)} percent`).join(', ')}`;

  return (
    <div
      {...rest}
      ref={ref}
      data-testid={dataTestId}
      className={withClassName(`tw-flex tw-flex-col tw-gap-2`, className)}
    >
      <div
        role="img"
        aria-label={spoken}
        data-testid={dataTestId ? `${dataTestId}-track` : undefined}
        /* `gap` rather than margins so the slices cannot drift apart at the ends,
           and `overflow-hidden` so the rounded track clips them into one pill. */
        className={`tw-flex tw-gap-px tw-w-full ${TRACK_H[size]} tw-rounded-full tw-overflow-hidden tw-bg-surface-sunken forced-colors:tw-border`}
      >
        {slices.map((slice, i) => (
          <div
            key={`${slice.label}-${i}`}
            data-testid={dataTestId ? `${dataTestId}-segment-${i}` : undefined}
            /* `currentColor` again: one text class colours the block, so the slice
               follows the theme with no second token. */
            className={`${seriesClass(slice.series ?? i)} tw-bg-current tw-h-full forced-colors:tw-bg-[color:Highlight]`}
            /* Inline width, necessarily — a percentage class cannot be built from a
               value the scanner never sees (the Progress reasoning). */
            style={{ width: `${slice.share}%` }}
          />
        ))}
      </div>

      {showLegend && slices.length > 0 && (
        <ul
          data-testid={dataTestId ? `${dataTestId}-legend` : undefined}
          /* The legend repeats what the bar's aria-label already said, so it is
             hidden from the a11y tree — otherwise every share is announced twice. */
          aria-hidden="true"
          className="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-m-0 tw-p-0 tw-list-none"
        >
          {slices.map((slice, i) => (
            <li key={`${slice.label}-legend-${i}`} className="tw-flex tw-items-center tw-gap-1.5">
              <span
                className={`${seriesClass(slice.series ?? i)} tw-bg-current tw-w-2 tw-h-2 tw-rounded-full tw-shrink-0`}
              />
              <span className="tw-text-caption-metadata tw-text-ink-secondary">{slice.label}</span>
              <span className="tw-text-caption-metadata tw-text-ink-tertiary tw-tabular-nums">
                {slice.share.toFixed(0)}%
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

export default DistributionBar;
